import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Bell, Menu, X } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api, mediaUrl } from "../lib/api";

function NavLink({ to, label, active, testid }) {
  return (
    <Link data-testid={testid} to={to}
          className={`hover:text-[#C9A227] transition ${active ? "text-[#C9A227]" : ""}`}>
      {label}
    </Link>
  );
}

function NotificationBell({ count }) {
  return (
    <Link to="/coach" data-testid="nav-bell" className="relative p-2 text-white/80 hover:text-[#C9A227] transition" title="Unanswered swings">
      <Bell size={18} />
      {count > 0 && (
        <span data-testid="nav-bell-count"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center">
          {count > 9 ? "9+" : count}
        </span>
      )}
    </Link>
  );
}

function AuthArea({ user, onLogout }) {
  if (user === undefined) return null;
  if (!user) {
    return (
      <>
        <Link to="/login" data-testid="nav-login" className="btn-ghost !py-2 !px-4 text-xs">Log in</Link>
        <Link to="/register" data-testid="nav-register" className="btn-primary !py-2 !px-4 text-xs">Start</Link>
      </>
    );
  }
  const avatarImg = mediaUrl(user.avatar_url);
  return (
    <>
      <Link to="/profile" data-testid="nav-profile" className="hidden sm:flex items-center gap-2 hover:opacity-80 transition">
        {avatarImg ? (
          <img src={avatarImg} alt="" data-testid="nav-avatar-img" className="w-7 h-7 rounded-full object-cover border border-[#C9A227]/40" />
        ) : (
          <span className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-[#050A08]"
                style={{ background: user.avatar_color || "#C9A227" }} data-testid="nav-avatar">
            {(user.name || user.email).slice(0, 1).toUpperCase()}
          </span>
        )}
        <span className="text-sm text-white/80" data-testid="nav-username">{user.name || user.email}</span>
        {user.role === "admin" && <span className="overline" style={{ color: "#C9A227" }}>Coach</span>}
      </Link>
      <button data-testid="nav-logout" onClick={onLogout} className="btn-ghost !py-2 !px-4 text-xs">Logout</button>
    </>
  );
}

export default function Nav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [unanswered, setUnanswered] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const canUpload = user?.is_pro || isAdmin;

  const links = [
    { to: "/feed", label: "Swing Feed", testid: "nav-feed", show: true },
    { to: "/pricing", label: "Membership", testid: "nav-pricing", show: true },
    { to: "/lesson", label: "In-Person · Trackman", testid: "nav-lesson", show: true },
    { to: "/upload", label: "Upload Swing", testid: "nav-upload", show: canUpload },
    { to: "/my-swings", label: "My Swings", testid: "nav-my-swings", show: !!user && !isAdmin },
    { to: "/profile", label: "My Profile", testid: "nav-profile-link", show: !!user },
    { to: "/coach", label: "Triage Bay", testid: "nav-coach", show: isAdmin },
    { to: "/admin", label: "Command Center", testid: "nav-admin", show: isAdmin },
  ].filter((l) => l.show);

  useEffect(() => {
    if (!isAdmin) { setUnanswered(0); return; }
    let cancelled = false;
    const poll = () => {
      api.get("/admin/stats")
        .then((r) => { if (!cancelled) setUnanswered(r.data.unanswered_videos || 0); })
        .catch((e) => console.error("Bell poll failed:", e));
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isAdmin]);

  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);

  const handleLogout = async () => { setMenuOpen(false); await logout(); nav("/"); };

  return (
    <header data-testid="nav-header" className="fixed top-0 inset-x-0 z-50 glass">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <circle cx="14" cy="14" r="13" stroke="#C9A227" strokeWidth="1.5"/>
            <path d="M5 18 Q14 8 23 14" stroke="#C9A227" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
            <circle cx="23" cy="14" r="1.5" fill="#C9A227"/>
          </svg>
          <div className="leading-none">
            <div className="overline">Dr.</div>
            <div className="font-serif text-xl font-semibold tracking-tight">Cuts</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm text-white/80">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} label={l.label} active={loc.pathname === l.to} testid={l.testid} />
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {isAdmin && <NotificationBell count={unanswered} />}
          <div className="hidden md:flex items-center gap-3">
            <AuthArea user={user} onLogout={handleLogout} />
          </div>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
            className="md:hidden p-2 text-white/80 hover:text-[#C9A227] transition">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div data-testid="nav-mobile-menu" className="md:hidden glass border-t border-white/10">
          <nav className="px-6 py-4 flex flex-col gap-1 text-white/85">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`mobile-${l.testid}`}
                onClick={() => setMenuOpen(false)}
                className={`py-3 border-b border-white/5 text-base transition hover:text-[#C9A227] ${loc.pathname === l.to ? "text-[#C9A227]" : ""}`}>
                {l.label}
              </Link>
            ))}
            <div className="pt-4 flex items-center gap-3">
              {user ? (
                <button data-testid="mobile-nav-logout" onClick={handleLogout} className="btn-ghost !py-2 !px-4 text-xs">Logout</button>
              ) : (
                <>
                  <Link to="/login" data-testid="mobile-nav-login" onClick={() => setMenuOpen(false)} className="btn-ghost !py-2 !px-4 text-xs">Log in</Link>
                  <Link to="/register" data-testid="mobile-nav-register" onClick={() => setMenuOpen(false)} className="btn-primary !py-2 !px-4 text-xs">Start</Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
